import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import Icon from 'components/AppIcon';
import { RADAR_DATA, ASSETS_ESG } from './esgData';

const SECTOR_COLORS = {
  'Renewable Energy': { bg: '#dcfce7', text: '#15803d' },
  'Water & Utilities': { bg: '#dbeafe', text: '#1d4ed8' },
  'Transport': { bg: '#fef3c7', text: '#b45309' },
  'Digital Infrastructure': { bg: '#ede9fe', text: '#7c3aed' },
};

function ScoreBar({ score, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-caption text-xs font-semibold text-foreground w-6 text-right">{score}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

function OverallPill({ score }) {
  const color = score >= 70 ? { bg: '#dcfce7', text: '#15803d' } : score >= 50 ? { bg: '#fef3c7', text: '#b45309' } : { bg: '#fee2e2', text: '#dc2626' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full font-caption text-xs font-semibold" style={{ background: color?.bg, color: color?.text }}>
      {score}
    </span>
  );
}

export default function ESGRadarAndAssets() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Radar Chart */}
      <div className="card lg:col-span-2" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>ESG Dimension Radar</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">Portfolio vs sector benchmark</p>
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={RADAR_DATA} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
            <PolarGrid stroke="#e2e8f0" />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#64748b' }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} tickCount={4} />
            <Radar name="Portfolio" dataKey="portfolio" stroke="#0f4c81" fill="#0f4c81" fillOpacity={0.25} strokeWidth={2} />
            <Radar name="Sector Benchmark" dataKey="benchmark" stroke="#0d9488" fill="#0d9488" fillOpacity={0.15} strokeWidth={1.5} strokeDasharray="4 3" />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
            <Tooltip formatter={(v) => `${v}/100`} contentStyle={{ fontSize: '11px', borderRadius: '8px' }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {/* Asset ESG Comparison Table */}
      <div className="card lg:col-span-3" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>Asset ESG Comparison</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">Individual asset scores — sample data</p>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontSize: '0.8125rem' }}>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>Asset</th>
                <th className="text-center pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>E</th>
                <th className="text-center pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>S</th>
                <th className="text-center pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>G</th>
                <th className="text-center pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>Overall</th>
                <th className="text-center pb-2 font-caption text-muted-foreground font-medium" style={{ fontSize: '0.75rem' }}>YoY</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ASSETS_ESG?.map((asset) => {
                const sectorStyle = SECTOR_COLORS?.[asset?.sector] || { bg: '#f1f5f9', text: '#475569' };
                return (
                  <tr key={asset?.name} className="hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        {asset?.alert && <Icon name="AlertTriangle" size={13} color="#dc2626" />}
                        <div>
                          <p className="font-caption font-medium text-foreground" style={{ fontSize: '0.8rem' }}>{asset?.name}</p>
                          <span className="inline-block px-1.5 py-0.5 rounded text-xs font-caption" style={{ background: sectorStyle?.bg, color: sectorStyle?.text, fontSize: '0.65rem' }}>{asset?.sector}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-2"><ScoreBar score={asset?.environmental} color="#16a34a" /></td>
                    <td className="py-2.5 px-2"><ScoreBar score={asset?.social} color="#2563eb" /></td>
                    <td className="py-2.5 px-2"><ScoreBar score={asset?.governance} color="#475569" /></td>
                    <td className="py-2.5 px-2 text-center"><OverallPill score={asset?.overall} /></td>
                    <td className="py-2.5 px-2 text-center">
                      <span className="font-caption text-xs font-semibold" style={{ color: asset?.yoy >= 0 ? '#16a34a' : '#dc2626' }}>
                        {asset?.yoy >= 0 ? '▲' : '▼'} {Math.abs(asset?.yoy)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
